import { memo } from "react";
import Markdown, { type Components, type Options } from "react-markdown";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import rehypeClassNames from "rehype-class-names";
import remarkGfm from "remark-gfm";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { articleImageStyles } from "../articleImageStyles";
import {
  type ArticleTableLayout,
  DEFAULT_ARTICLE_TABLE_LAYOUT,
  tableScrollContainerStyles,
  tableStyles,
} from "../articleTableStyles";

export const editorTheme = {
  paragraph: "editor-paragraph",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
  },
  list: {
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
  },
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
  },
};

const rehypeEditorTheme = {
  h1: editorTheme.heading.h1,
  h2: editorTheme.heading.h2,
  h3: editorTheme.heading.h3,
  ol: editorTheme.list.ol,
  ul: editorTheme.list.ul,
  li: editorTheme.list.listitem,
  b: editorTheme.text.bold,
  em: editorTheme.text.italic,
  p: editorTheme.paragraph,
  underline: editorTheme.text.underline,
};

const remarkPlugins: NonNullable<Options["remarkPlugins"]> = [remarkGfm];
const rehypePlugins: NonNullable<Options["rehypePlugins"]> = [
  [rehypeClassNames, rehypeEditorTheme],
];

/**
 * The wrapper is focusable under both layouts: it can still scroll under `fit`
 * (a table too wide to wrap hits the cell floor), and a scrollable region has
 * to be keyboard-reachable. Only the label changes, since "Scrollable" is not
 * true of most tables once they fit their column.
 */
const tableComponentsFor = (label: string): Components => ({
  table: ({ node: _node, ...tableProps }) => (
    <TableScrollContainer
      aria-label={label}
      data-testid="markdown-table-scroll"
      role="region"
      tabIndex={0}
    >
      <table {...tableProps} />
    </TableScrollContainer>
  ),
});

const markdownComponents: Record<ArticleTableLayout, Components> = {
  scroll: tableComponentsFor("Scrollable table"),
  fit: tableComponentsFor("Table"),
};

const demotedH1Component: Components = {
  h1: ({ node: _node, ...props }) => <h2 {...props} />,
};

export const DreamMarkdownReader = memo(
  ({
    value,
    "data-testid": dataTestid,
    sx,
    tableLayout = DEFAULT_ARTICLE_TABLE_LAYOUT,
    demoteH1 = false,
    ...propsToBox
  }: Omit<React.ComponentProps<typeof Box>, "children"> & {
    value?: string;
    "data-testid"?: string;
    /**
     * Defaults to `scroll`, which is what every caller outside the blog wants:
     * their tables are narrow enough that wrapping only costs vertical space.
     */
    tableLayout?: ArticleTableLayout;
    /** Keeps authored copy intact while preserving a page-level single-H1 outline. */
    demoteH1?: boolean;
  }) => {
    const sxProps = Array.isArray(sx) ? sx : [sx];

    return (
      <Box
        {...propsToBox}
        sx={[{ width: "100%" }, ...sxProps]}
        {...(dataTestid && { "data-testid": kebabCase(dataTestid) })}
      >
        <Styles $tableLayout={tableLayout}>
          <Markdown
            components={
              demoteH1
                ? { ...markdownComponents[tableLayout], ...demotedH1Component }
                : markdownComponents[tableLayout]
            }
            rehypePlugins={rehypePlugins}
            remarkPlugins={remarkPlugins}
          >
            {value}
          </Markdown>
        </Styles>
      </Box>
    );
  }
);

const TableScrollContainer = styled("div")(
  ({ theme }) => `
  ${tableScrollContainerStyles(theme)}
`
);

const Styles = styled("div", {
  shouldForwardProp: (prop) => prop !== "$tableLayout",
})<{ $tableLayout: ArticleTableLayout }>(
  ({ theme, $tableLayout }) => `
  width: 100%;
  font-size: 1em;
  line-height: 150%;
  position: relative;
  outline: 0;
  color: ${theme.palette.text.primary};

  .editor-text-bold {
    font-weight: bold;
  }

  .editor-text-italic {
    font-style: italic;
  }

  .editor-text-underline {
    text-decoration: underline;
  }

  .editor-text-strikethrough {
    text-decoration: line-through;
  }

  .editor-text-underlineStrikethrough {
    text-decoration: underline line-through;
  }

  .editor-text-code {
    background-color: rgb(240, 242, 245);
    padding: 1px 0.25rem;
    font-family: Menlo, Consolas, Monaco, monospace;
  }

  .editor-paragraph {
    margin: 0;
    margin-bottom: 1em;
    position: relative;
  }

  .editor-paragraph:last-child {
    margin-bottom: 0;
  }

  .editor-heading-h1 {
    font-size: 2em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 400;
    margin: 0 0 12px;
    padding: 0;
  }

  .editor-heading-h2 {
    font-size: 1.5em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 700;
    padding: 0;
  }

  .editor-heading-h3 {
    font-size: 1.2em;
    line-height: 140%;
    color: ${theme.palette.text.primary};
    font-weight: 700;
    padding: 0;
  }

  .editor-list-ol {
    padding: 0;
    margin: 0;
    margin-left: 16px;
  }

  .editor-list-ul {
    padding: 0;
    margin: 0;
    margin-left: 16px;
  }

  .editor-listitem {
    margin: 8px 32px 8px 32px;

    & li {
      list-style-type: none;
    }
  }

  pre::-webkit-scrollbar {
    background: transparent;
    width: 10px;
  }

  pre::-webkit-scrollbar-thumb {
    background: #999;
  }

  ${articleImageStyles}
  ${tableStyles(theme, $tableLayout)}
`
);
